import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Plus, MessageSquare, ArrowRightLeft, Download, StickyNote, ChevronDown, ChevronUp, MoreHorizontal, CheckCircle2, GraduationCap, XCircle, MessageCircle, X, Phone, Lock, FilterX } from "lucide-react";
import * as XLSX from "xlsx";
// The app's one table. This page keeps its own `eq-*` stylesheet — the
// Tailwind conversion is a separate, larger job (33 classes across two sheets),
// so nothing here uses a `tw-` class and `--check mixed` stays green.
import {
  StatusBadge, AppBadge, EmptyState, AppButton, ConfirmModal, ActionMenu, StatsCard,
  StatsGrid, AppDataTable, FilterToolbar,
} from "../ui";
import { EnquirySourceBadge, resolveEnquirySource } from "./enquirySource";
import useEnquiryActions from "./useEnquiryActions";
import useEnquiryStages from "../../hooks/useEnquiryStages";
import useEnquiryOwnership from "../../hooks/useEnquiryOwnership";
import FollowUpDue from "./FollowUpDue";
import "./follow-up.scss";
import EnquiryContextMenu from "./EnquiryContextMenu";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import { useSocket } from "../../contexts/SocketContext";
import { useBranch } from "../../contexts/BranchContext";
import TransferBranchModal from "../Common/TransferBranchModal";
import EnquiryWhatsAppModal from "./EnquiryWhatsAppModal";
import { usePlan } from "../../contexts/PlanContext";
import { hasPermission } from "../../utils/permissions";
import { can } from "../../utils/access";
import "./enquiries-list.scss";

// Statuses now come from the institute's configured pipeline via
// useEnquiryStages — see the hook for why the list is no longer hardcoded here.

const SOURCE_OPTIONS = [
  { value: "manual", label: "Manual" },
  { value: "website", label: "Website" },
  { value: "embed_form", label: "Embed Form" },
  { value: "google_ads", label: "Google Ads" },
  { value: "meta_ads", label: "Meta Ads" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "zoho", label: "Zoho CRM" },
  { value: "all_except_zoho", label: "Except Zoho" },
  { value: "other", label: "Other" },
];

const ENTRIES_PER_PAGE = 10;
/* ⚠️ The version bump is the migration. A restored filter outranks a changed
   default, so a new default only reaches a tab that already had the page open
   through a new key. v2 dropped the `assignedTo: <self>` default (see
   `getDefaultFilters`); v3 replaced the calendar presets with rolling windows,
   and a stored "this month" range matches none of them, so it read as Custom.
   sessionStorage is per-tab, so the cost is one forgotten filter set. */
const FILTER_STORAGE_KEY = 'eq_filters_v3';

/** Zoho's sync marker → an AppBadge colour. Anything else is `secondary`. */
const ZOHO_SYNC_COLOR = {
  synced: 'emerald',
  pending: 'amber',
  failed: 'rose',
};

/**
 * Column key → the `sortBy` the list endpoint accepts.
 *
 * ⚠️ The list is SERVER-paginated, so a header arrow has to reorder the LIST,
 * not the ten rows in memory. A column absent here gets no arrow, because
 * `getEnquiries` whitelists exactly these — offering to sort by a field the
 * server ignores is worse than not offering it: the arrow moves and nothing
 * does.
 */
const SORT_PARAM = {
  name: 'name',
  contact: 'mobile',
  status: 'status',
  date: 'createdAt',
  followUp: 'nextFollowUpAt',
};

/**
 * Column key → the server filter it drives, and how to read/write it.
 *
 * Every one of these is a real parameter on `/enquiries/list`, which is what
 * makes an in-header funnel honest on a server-paginated table: the value goes
 * upstream and the server narrows the whole list. A column whose field the
 * endpoint does not accept deliberately has no funnel.
 */
const COLUMN_FILTERS = {
  // The endpoint's `search` covers full name, email and course name.
  name: { toValue: (f) => f.search, toFilters: (v) => ({ search: v || '' }) },
  contact: { toValue: (f) => f.mobile, toFilters: (v) => ({ mobile: v || '' }) },
  course: { toValue: (f) => (f.course ? [f.course] : []), toFilters: (v) => ({ course: v?.[0] || '' }) },
  source: { toValue: (f) => (f.source ? [f.source] : []), toFilters: (v) => ({ source: v?.[0] || '' }) },
  // The endpoint matches campaign NAME or id, partially — hence text, not a set.
  campaign: { toValue: (f) => f.campaign, toFilters: (v) => ({ campaign: v || '' }) },
  status: { toValue: (f) => (f.status ? [f.status] : []), toFilters: (v) => ({ status: v?.[0] || '' }) },
  assigned: { toValue: (f) => (f.assignedTo ? [f.assignedTo] : []), toFilters: (v) => ({ assignedTo: v?.[0] || '' }) },
  date: {
    toValue: (f) => (f.startDate || f.endDate ? { from: f.startDate, to: f.endDate } : undefined),
    toFilters: (v) => ({ startDate: v?.from || '', endDate: v?.to || '' }),
  },
};

// ── Date-range presets (drive the KPI cards + the table's date filter) ──
const pad2 = (n) => String(n).padStart(2, "0");
const fmtDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/* Rolling windows ending today, not calendar periods. "This Month" on the 2nd
   was two days of data compared against the two days before it; "30d" is the
   same span every day, and it is the span `/enquiries/stats` compares against
   (the equal-length window immediately before), so the KPI trend is honest.
   `days` counts today: 1d is today, 7d is today and the six days before it. */
const RANGE_PRESETS = [
  { key: "1d", label: "1d", name: "Today", days: 1 },
  { key: "3d", label: "3d", name: "Last 3 days", days: 3 },
  { key: "7d", label: "7d", name: "Last 7 days", days: 7 },
  { key: "30d", label: "30d", name: "Last 30 days", days: 30 },
  { key: "90d", label: "90d", name: "Last 90 days", days: 90 },
  { key: "all", label: "All", name: "All time" },
];
const DEFAULT_PRESET = "30d";

// Returns { startDate, endDate } as local YYYY-MM-DD for a preset key.
function presetRange(key) {
  const days = RANGE_PRESETS.find((p) => p.key === key)?.days;
  if (!days) return { startDate: "", endDate: "" };
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (days - 1));
  return { startDate: fmtDate(start), endDate: fmtDate(today) };
}

// Which preset (if any) the current start/end dates correspond to.
function activePreset(startDate, endDate) {
  if (!startDate && !endDate) return "all";
  for (const p of RANGE_PRESETS) {
    if (p.key === "all") continue;
    const r = presetRange(p.key);
    if (r.startDate === startDate && r.endDate === endDate) return p.key;
  }
  return "custom";
}

// "12%"-style delta vs the previous period; null when not comparable.
function pctChange(cur, prev) {
  if (prev == null || prev === 0) return null;
  return `${Math.abs(Math.round(((cur - prev) / prev) * 100))}%`;
}

/**
 * The filters the page opens on.
 *
 * ⚠️ **No default assignee, in any mode.** This used to pre-apply
 * `assignedTo: <me>` for anyone whose `usertype` was `admin` and who was not an
 * institute Super Admin — a guess made from localStorage, taken without ever
 * asking whether the institute had Enquiry Ownership Control switched on. On
 * the institutes that had it OFF, an admin entitled to the whole pipeline was
 * shown one slice of it and told nothing; when that slice was empty the page
 * rendered "No enquiries found" *in place of the table*, so the filter doing
 * the hiding was the one control that had been unmounted.
 *
 * It is not reinstated for the restricted case either, and that is deliberate.
 * Under ownership control `_applyVisibilityFilter` narrows the query on the
 * SERVER and overrides whatever `assignedTo` the client sends — so the same
 * filter here is a no-op that costs a "Clear filters" button which clears it
 * and changes nothing on screen. The mode is a policy, not a filter, so it is
 * reported as a policy: see the scope notice in the toolbar.
 */
function getDefaultFilters() {
  const { startDate, endDate } = presetRange(DEFAULT_PRESET);
  return {
    search: "",
    status: "",
    course: "",
    mobile: "",
    startDate,
    endDate,
    source: "",
    campaign: "",
    assignedTo: "",
  };
}

function loadFilters() {
  try {
    const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
    // Merged over the defaults, never used raw: a blob written by an older
    // build can be missing a key, and a control reading `undefined` where it
    // expects "" is an uncontrolled input one render later.
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed && typeof parsed === 'object') return { ...getDefaultFilters(), ...parsed };
    }
  } catch { /* ignore */ }
  return getDefaultFilters();
}

const EnquiriesList = () => {
  const [filters, setFilters] = useState(loadFilters);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(ENTRIES_PER_PAGE);
  // Server-side, like every other control on this page. Null = the endpoint's
  // own default, newest first.
  const [sort, setSort] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState(() => new Set());
  const [showAllNotes, setShowAllNotes] = useState(false);
  const toggleNoteRow = (id) =>
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const [totalPages, setTotalPages] = useState(1);
  const [courses, setCourses] = useState([{ value: "", label: "All Courses" }]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState([]);
  const [showBranchTransfer, setShowBranchTransfer] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const { selectedBranchId, isMultiBranch, branches } = useBranch();
  const { hasPlanFeature } = usePlan();
  const canWhatsApp = hasPlanFeature("whatsapp");
  const canEdit = hasPermission("add_edit_enquiries");
  const canConvert = can("enquiries.convert");
  const canChangeBranch = isMultiBranch && branches.length > 1 && hasPermission("manage_branches");

  // Selection holds ids; the modal needs the rows themselves. Only the current
  // page is in memory, which is also the only page the user can tick.
  const selectedEnquiries = enquiries.filter((e) => selected.includes(e._id));

  const [contextMenu, setContextMenu] = useState(null); // { enquiry, x, y }

  const navigate = useNavigate();

  // The institute's configured pipeline, replacing what used to be a hardcoded
  // array duplicated across three screens.
  const { stageNames } = useEnquiryStages();

  /* Enquiry Ownership Control. `canDelete` gates the row action; `restricted`
     drives the scope notice and the "why is this empty" line in the empty
     state.
     ⚠️ Gate BOTH on `!ownershipLoading`. The hook's pre-answer value is
     `DENIED`, which reads `restricted: true` — so an ungated notice claims
     ownership control is on for one paint on every institute, the Super
     Admin's included. */
  const {
    canDelete,
    restricted,
    isSuperAdmin,
    unresolved,
    loading: ownershipLoading,
  } = useEnquiryOwnership();
  const scopedToSelf = !ownershipLoading && restricted && !isSuperAdmin;
  /* The worst version of "scoped to yourself": the server could not match this
     login to an Admin record, so the ownership filter matches NOTHING and this
     list is empty permanently — no filter to clear, no date range to widen.
     Without this the page renders a perfectly convincing empty pipeline. */
  const scopeUnresolved = scopedToSelf && unresolved;

  // Shared row actions (kebab dropdown + right-click context menu).
  const actions = useEnquiryActions({
    canDelete: canDelete && canEdit,
    canEdit,
    canConvert,
    onDelete: (id) => handleDelete(id),
    onNoteAdded: () => setRefreshKey((k) => k + 1),
    onConverted: () => {
      setToast({ show: true, message: "Enquiry moved to Students successfully!", type: "success" });
      setRefreshKey((k) => k + 1);
    },
  });

  const openContextMenu = (enquiry, e) => {
    e.preventDefault();
    setContextMenu({ enquiry, x: e.clientX, y: e.clientY });
  };

  // Persist filters to sessionStorage whenever they change
  useEffect(() => {
    try { sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters)); } catch { /* ignore */ }
  }, [filters]);

  // Date range is represented by the segmented preset control, so it's excluded
  // from the chip list + filter badge to avoid duplicating that state.
  // Drives the toolbar's "Clear filters" button — the one thing on screen that
  // says a column funnel is hiding rows. `search` counts too now: it is the Name
  // column's filter rather than a separate box.
  const activeFilterCount = [
    filters.search, filters.status, filters.course, filters.mobile,
    filters.source, filters.campaign, filters.assignedTo,
  ].filter(Boolean).length;

  /**
   * Clear the column funnels.
   *
   * ⚠️ The date range is NOT one of them by default. It is its own control
   * above the table and is deliberately absent from `activeFilterCount`, so a
   * button labelled by that count must not move it — this used to snap the
   * range back to "This Month", quietly NARROWING the view of anyone who had
   * picked This Year and then cleared a funnel.
   *
   * `dates: true` is the escape hatch, and only the empty state passes it: when
   * the table has nothing in it the range is a suspect like any other filter,
   * and the one button on screen has to be able to reach every one of them.
   */
  const handleResetFilters = ({ dates = false } = {}) => {
    setFilters((f) => ({
      ...f, search: "", status: "", course: "", mobile: "",
      source: "", campaign: "", assignedTo: "",
      ...(dates ? { startDate: "", endDate: "" } : {}),
    }));
    setCurrentPage(1);
  };

  // Date-range preset control (drives both the KPI cards and the table).
  const currentPreset = activePreset(filters.startDate, filters.endDate);
  const applyPreset = (key) => {
    const r = presetRange(key);
    setFilters((f) => ({ ...f, startDate: r.startDate, endDate: r.endDate }));
  };

  // A date range narrower than All Time is a filter too. It is not in
  // `activeFilterCount` because it has its own control above the table, but the
  // empty state has to name it: the default 30d is the single most common reason a
  // list that has enquiries in it looks like a list that has none.
  const dateNarrowed = currentPreset !== "all";
  const narrowed = activeFilterCount > 0 || dateNarrowed;

  /**
   * The active filters, in words, for the empty state.
   *
   * An empty table's real question is "why", and neither a funnel icon nor a
   * count answers it — the funnels are eleven columns wide and the one that is
   * set may be scrolled off the side of the card. Naming them is the answer,
   * and it is also what makes the Clear button below the list read as a
   * consequence rather than a guess.
   */
  const filterSummary = useMemo(() => {
    const labelFor = (options, value) =>
      options.find((o) => String(o.value) === String(value))?.label || value;
    const assigneeLabel = (id) =>
      id === "unassigned"
        ? "Unassigned"
        : labelFor(
            assignees.map((a) => ({
              value: String(a._id),
              label: [a.firstName, a.lastName].filter(Boolean).join(" ") || a.email,
            })),
            id
          );

    const parts = [];
    if (filters.search) parts.push(`Name, email or course: “${filters.search}”`);
    if (filters.mobile) parts.push(`Mobile: “${filters.mobile}”`);
    if (filters.course) parts.push(`Course: ${labelFor(courses, filters.course)}`);
    if (filters.source) parts.push(`Source: ${labelFor(SOURCE_OPTIONS, filters.source)}`);
    if (filters.campaign) parts.push(`Campaign: “${filters.campaign}”`);
    if (filters.status) parts.push(`Status: ${filters.status}`);
    if (filters.assignedTo) parts.push(`Assigned: ${assigneeLabel(filters.assignedTo)}`);
    if (dateNarrowed) {
      parts.push(
        `Date: ${RANGE_PRESETS.find((r) => r.key === currentPreset)?.name || "Custom range"}`
      );
    }
    return parts;
  }, [filters, courses, assignees, currentPreset, dateNarrowed]);

  /* One builder for `/enquiries/list`, because two effects call it — the normal
     fetch and the socket-driven refetch. They were two copies of the same
     object literal, which is how a parameter gets added to one and not the
     other and the list quietly disagrees with itself after a live update. */
  // `useCallback` so the two effects below can depend on it honestly. A fresh
  // function each render would either go stale in their dependency arrays or
  // refetch on every paint.
  const buildListParams = useCallback(() => ({
    ...filters,
    page: currentPage,
    limit: pageSize,
    // Only when the column is one the endpoint whitelists — see SORT_PARAM.
    ...(sort && SORT_PARAM[sort.key]
      ? { sortBy: SORT_PARAM[sort.key], sortOrder: sort.direction }
      : {}),
  }), [filters, currentPage, pageSize, sort]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const params = buildListParams();

      // The two requests are settled independently on purpose. They used to
      // share one try/catch around a Promise.all, which meant the courses
      // dropdown failing raised "Failed to load enquiries" over a list that had
      // loaded perfectly well — and, under Enquiry Ownership Control, an admin
      // whose queue is simply empty read that as the page being broken.
      const [eRes, cRes] = await Promise.allSettled([
        apiClient.get("/enquiries/list", { params }),
        apiClient.get("/courses/list"),
      ]);

      if (eRes.status === "fulfilled") {
        const eData = eRes.value.data || {};
        // An empty page is a normal answer, never an error: a restricted admin
        // with nothing assigned to them gets the empty table, not a toast.
        setEnquiries(eData.enquiries || []);
        setTotalPages(eData.totalPages || 1);
        setTotalRecords(eData.total || 0);
      } else {
        setEnquiries([]);
        setTotalPages(1);
        setTotalRecords(0);
        setToast({
          show: true,
          message: eRes.reason?.response?.data?.message || "Failed to load enquiries.",
          type: "error",
        });
      }

      if (cRes.status === "fulfilled") {
        setCourses([
          { value: "", label: "All Courses" },
          ...(cRes.value.data?.courses || []).map((c) => ({
            value: c._id,
            label: c.name,
          })),
        ]);
      }

      setLoading(false);
    };
    fetchData();
    // `sort` is part of the request, so a header click has to refetch — the
    // ordering is the server's, not this page's.
    // eslint-disable-next-line
  }, [filters, currentPage, pageSize, selectedBranchId, refreshKey, sort]);

  // Fetch assignees list for the filter dropdown
  useEffect(() => {
    apiClient.get("/enquiries/assignees")
      .then(({ data }) => setAssignees(data.admins || []))
      .catch(() => {});
  }, []);

  // ── Funnel KPI stats (cards) — follow every filter except status ──
  useEffect(() => {
    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        // eslint-disable-next-line no-unused-vars
        const { status, ...rest } = filters; // cards show the full funnel regardless of the status filter
        const { data } = await apiClient.get("/enquiries/stats", { params: rest });
        setStats(data);
      } catch {
        setStats(null);
      }
      setStatsLoading(false);
    };
    fetchStats();
    // eslint-disable-next-line
  }, [filters.search, filters.course, filters.mobile, filters.startDate, filters.endDate, filters.source, filters.campaign, filters.assignedTo, selectedBranchId, refreshKey]);

  // ── Live-reload when a new enquiry arrives via socket ──
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;

    /**
     * Refresh the list under whoever is already looking at it.
     *
     * ⚠️ **No sound and no toast here.** `notification:new` is handled globally
     * in SocketContext, which already plays the sound and raises the corner
     * toast — doing it again on this page would double the sound and stack two
     * cards saying the same thing. This listener exists only so the row appears
     * without a manual refresh.
     *
     * It covers every intake path because it keys on the event the backend
     * sends for all of them; the older `lead:new` is ad-ingestion only and a
     * website form would have been missed.
     */
    const handleNewEnquiry = (data = {}) => {
      if (data.type !== 'enquiry_new') return;
      setRefreshKey(k => k + 1);
    };

    socket.on('notification:new', handleNewEnquiry);
    return () => socket.off('notification:new', handleNewEnquiry);
  }, [socket]);

  // Re-fetch when refreshKey changes (new enquiry arrived)
  useEffect(() => {
    if (refreshKey === 0) return; // skip initial
    const reFetch = async () => {
      try {
        const params = buildListParams();
        const { data: eData } = await apiClient.get("/enquiries/list", { params });
        setEnquiries(eData.enquiries || []);
        setTotalPages(eData.totalPages || 1);
        setTotalRecords(eData.total || 0);
      } catch { /* silent */ }
    };
    reFetch();
    // eslint-disable-next-line
  }, [refreshKey]);

  // Reset to page 1 if filters change (except currentPage)
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line
  }, [JSON.stringify(filters)]);

  // Selection only ever refers to rows currently on screen — bulk actions resolve
  // ids against the loaded page, so carrying ticks across a page or branch change
  // would silently drop recipients from the count shown on the button.
  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    setSelected([]);
  }, [currentPage, pageSize, selectedBranchId, filtersKey]);

  const handleDelete = async (id) => {
    if (!id) return;
    setDeleteTarget(id);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/enquiries/${deleteTarget}`);
      setToast({
        show: true,
        message: "Enquiry deleted successfully!",
        type: "success",
      });
      setRefreshKey(k => k + 1);
    } catch {
      setToast({
        show: true,
        message: "Failed to delete enquiry.",
        type: "error",
      });
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = { ...filters, page: 1, limit: 10000 };
      const { data } = await apiClient.get("/enquiries/list", { params });
      const rows = (data.enquiries || []).map((e) => ({
        ID: e._id,
        Name: e.fullName || "",
        Email: e.email || "",
        Mobile: e.mobile || "",
        Course: e.course?.name || "",
        Status: e.status || "",
        Source: resolveEnquirySource(e).label,
        Campaign: e.campaign?.name || "",
        "Assigned To": e.assignedTo
            ? `${e.assignedTo.firstName || ''} ${e.assignedTo.lastName || ''}`.trim() || e.assignedTo.email || ''
            : "",
        Branch: e.branchId?.name || "",
        Notes: Array.isArray(e.followUpNotes)
          ? e.followUpNotes.map((n) => `${n.note}`).join(" | ")
          : "",
        "Created At": e.createdAt ? new Date(e.createdAt).toLocaleString() : "",
      }));

      if (rows.length === 0) {
        setToast({ show: true, message: "No enquiries to export.", type: "info" });
        return;
      }

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Enquiries");
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `enquiries-${stamp}.xlsx`);
      setToast({ show: true, message: `Exported ${rows.length} enquiries.`, type: "success" });
    } catch {
      setToast({ show: true, message: "Failed to export enquiries.", type: "error" });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="enquiries-page">
      {/* The page header and the Enquiries/Calls tab rail belong to
          EnquiryWorkspace, the layout route above this one — a second header here
          would stack two titles and cost a row on a viewport where vertical space
          is the constraint. */}

      {/* ── Funnel KPI overview (filter-responsive, foldable) ──
          The page is a bounded column now, so this row costs the table roughly
          half its visible rows. `collapsible` folds it and remembers the
          choice; the head line it renders replaced this page's own
          `eq-overview__head`.

          ⚠️ The date-range presets are `actions`, not children. They drive the
          TABLE's date filter as well as the cards, so they have to stay
          reachable when the cards are folded away. */}
      <StatsGrid
        collapsible
        storageKey="enquiries"
        title="Pipeline overview"
        actions={
          <div className="eq-range" role="group" aria-label="Date range">
            {RANGE_PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`eq-range__btn ${currentPreset === p.key ? "is-active" : ""}`}
                aria-label={p.name}
                aria-pressed={currentPreset === p.key}
                title={p.name}
                onClick={() => applyPreset(p.key)}
              >
                {p.label}
              </button>
            ))}
            {currentPreset === "custom" && (
              <span className="eq-range__btn is-active is-custom" title="Custom range from filters">Custom</span>
            )}
          </div>
        }
      >
          {statsLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <StatsCard key={i} loading />
            ))
          ) : (
            <>
              <StatsCard
                title="Total Enquiries"
                value={stats?.total ?? 0}
                icon={MessageSquare}
                iconColor="blue"
                change={pctChange(stats?.total, stats?.previous?.total)}
                changeType={(stats?.total ?? 0) >= (stats?.previous?.total ?? 0) ? "positive" : "negative"}
              />
              <StatsCard
                title="Confirmed"
                value={stats?.confirmed ?? 0}
                icon={CheckCircle2}
                iconColor="cyan"
                change={pctChange(stats?.confirmed, stats?.previous?.confirmed)}
                changeType={(stats?.confirmed ?? 0) >= (stats?.previous?.confirmed ?? 0) ? "positive" : "negative"}
              />
              <StatsCard
                title="Joined"
                value={stats?.joined ?? 0}
                icon={GraduationCap}
                iconColor="emerald"
                change={pctChange(stats?.joined, stats?.previous?.joined)}
                changeType={(stats?.joined ?? 0) >= (stats?.previous?.joined ?? 0) ? "positive" : "negative"}
              />
              <StatsCard
                title="Rejected"
                value={stats?.rejected ?? 0}
                icon={XCircle}
                iconColor="rose"
                change={pctChange(stats?.rejected, stats?.previous?.rejected)}
                changeType={(stats?.rejected ?? 0) <= (stats?.previous?.rejected ?? 0) ? "positive" : "negative"}
              />
            </>
          )}
      </StatsGrid>

      {/* Toolbar: Filters (left) — Actions (right) */}
      {/* ⚠️ The search box, the FilterButton, the FilterPanel drawer and the
          ActiveFilterChips that used to sit here are GONE (owner, 2026-09-03):
          every list control now lives inside the table card — filters in the
          column headers, the row actions in AppDataTable's `toolbar`.

          The drawer's fields each became the matching column's filter, and its
          Campaign text input became a Campaign COLUMN, so nothing it could set
          became unreachable. The date range presets above stay: they drive the
          KPI cards as well as the table, and they write the same `filters`
          object the Date column's funnel does, so the two cannot disagree. */}

      {/* Table. `loading` goes to DataTable, which has rendered a real
          <tr>/<td> Shimmer skeleton since Phase 6.5 — it keeps the column
          geometry so the page does not reflow when the rows land. Gating it
          behind a spinner meant this page opted out of the skeleton 23 others
          get.

          ⚠️ The table is rendered UNCONDITIONALLY. An empty result used to
          replace it with a bare `EmptyState`, which took the column funnels,
          the toolbar and its "Clear filters" button down with it — so the
          filter that emptied the list became the one thing on the page that
          could not be reached, and the only way out was a browser reload (and
          not even that: the filters are in sessionStorage). The empty state now
          lives INSIDE the card, as `AppDataTable`'s `emptyState`, with the
          headers still above it. */}
            <AppDataTable
              rows={enquiries}
              rowKey={(e) => e._id}
              /* The page is a bounded column now (`app-page-fill`, applied by
                 EnquiryWorkspace for this tab), so the card takes whatever is
                 left below the KPI block and scrolls its OWN body between the
                 sticky header and the pinned paginator. That is also what puts
                 the horizontal scrollbar under the last row instead of at the
                 bottom of a twenty-row page. */
              fill
              loading={loading}
              /* Rendered inside the card, under the live column headers — so
                 whatever emptied the list is still on screen and still
                 clearable. `AppDataTable` pins it to the scroll port, which a
                 `colSpan` cell on a table this wide otherwise does not do. */
              emptyState={
                <EmptyState
                  icon={<MessageSquare size={36} />}
                  title={
                    scopeUnresolved
                      ? "We can't tell which admin you are"
                      : narrowed
                      ? "No enquiries match these filters"
                      : "No enquiries yet"
                  }
                  /* ⚠️ The ownership sentence is appended to BOTH branches, not
                     used as an alternative to them. The page opens on "This
                     Month", so `narrowed` is true on a restricted admin's very
                     first visit — the branch that would have carried it alone
                     is the one they are least likely to see. */
                  description={
                    scopeUnresolved
                      ? "Ownership control is on, and your login is not matched to an admin profile in this institute — so no enquiry can ever match. Clearing filters will not help. Ask your Super Admin to check your admin record, usually a mismatched email address."
                      : [
                          narrowed
                            ? "Nothing in the current view. Widen or clear the filters to see the rest."
                            : "New enquiries will show up here as they arrive.",
                          scopedToSelf &&
                            "Ownership control is on for your institute, so this list only ever shows enquiries assigned to you.",
                        ]
                          .filter(Boolean)
                          .join(" ")
                  }
                  action={
                    scopeUnresolved ? null : (
                    <div className="eq-empty__actions">
                      {narrowed && filterSummary.length > 0 && (
                        <ul className="eq-empty__chips" aria-label="Filters currently applied">
                          {filterSummary.map((part) => (
                            <li key={part} className="eq-empty__chip">{part}</li>
                          ))}
                        </ul>
                      )}
                      {narrowed ? (
                        /* Clears the funnels AND the date range — the one
                           button on an empty table has to be able to undo
                           every reason the table is empty, or it is another
                           dead end. */
                        <AppButton size="sm" onClick={() => handleResetFilters({ dates: true })}>
                          <FilterX size={14} /> Clear all filters
                        </AppButton>
                      ) : canEdit && (
                        /* Shown to a restricted admin too: `addEnquiry` assigns
                           what they create to them, so an empty queue is
                           precisely the state from which adding one helps. */
                        <AppButton size="sm" onClick={() => navigate("/enquiries/new")}>
                          <Plus size={14} /> Add Enquiry
                        </AppButton>
                      )}
                    </div>
                    )
                  }
                />
              }
              resizable
              storageKey="enquiries"
              /* Sorting and filtering are BOTH server-side here — the page is
                 one page of the list, so the header controls send values
                 upstream instead of reordering the ten rows in memory. That is
                 what `sort` / `columnFilters` being controlled is for. */
              toolbar={
                <FilterToolbar
                  flush
                  actions={
                    <>
                      <AppButton
                        variant="outline"
                        size="sm"
                        onClick={() => setShowAllNotes((v) => !v)}
                        title={showAllNotes ? "Hide notes for all rows" : "Show notes for all rows"}
                      >
                        <StickyNote size={14} /> {showAllNotes ? "Hide notes" : "Show notes"}
                      </AppButton>
                      <AppButton variant="secondary" size="sm" onClick={handleExport} disabled={exporting || loading}>
                        {exporting ? <Spinner animation="border" size="sm" /> : <Download size={16} />}
                        {exporting ? " Exporting..." : " Export"}
                      </AppButton>
                      {canEdit && (
                        <AppButton size="sm" onClick={() => navigate("/enquiries/new")}>
                          <Plus size={16} /> Add Enquiry
                        </AppButton>
                      )}
                    </>
                  }
                >
                  {/* ⚠️ `eq-cell-muted` set a colour and nothing else, so this
                      count inherited `body`'s size — and `body` is Bootstrap's
                      `--bs-body-font-size: 1rem`, never overridden. It rendered
                      at 16px beside 13px buttons and a 13px table. Nothing here
                      may rely on inheriting a size: the app's default is a
                      Bootstrap value, not the type scale's 14px base. */}
                  <span className="eq-toolbar-count">
                    {totalRecords} enquir{totalRecords === 1 ? "y" : "ies"}
                  </span>
                  {/* A POLICY, not a filter — which is exactly why it is not a
                      chip beside "Clear filters". Under ownership control the
                      server narrows the query itself and ignores whatever
                      `assignedTo` the client sends, so there is nothing here to
                      clear; saying so is the only way the count above stops
                      reading as the institute's whole pipeline. */}
                  {scopedToSelf && (
                    <span
                      className={`eq-scope-note${scopeUnresolved ? " eq-scope-note--broken" : ""}`}
                      title={
                        scopeUnresolved
                          ? "Ownership control is on, but your login is not matched to an admin profile in this institute, so nothing can be shown. Ask your Super Admin to check your admin record."
                          : "Enquiry Ownership Control is on for your institute. Only your Super Admin can change this."
                      }
                    >
                      <Lock size={12} aria-hidden="true" />{" "}
                      {scopeUnresolved ? "Admin profile not matched" : "Assigned to you only"}
                    </span>
                  )}
                  {/* A funnel in a header is easy to set and easy to forget —
                      this button is the only thing on screen that says a column
                      is hiding rows.
                      ⚠️ The handler is WRAPPED, not passed by reference: it
                      takes an options object and a bare `onClick` would hand it
                      the click event instead. */}
                  {activeFilterCount > 0 && (
                    <AppButton size="sm" variant="outline" onClick={() => handleResetFilters()}>
                      <X size={14} /> Clear filters
                    </AppButton>
                  )}
                  {selected.length > 0 && (
                    <>
                      <span className="eq-selection-pill">
                        {selected.length} selected
                        {/* `title` is a tooltip; a screen reader is not required
                            to read it, so an icon-only button needs a real name. */}
                        <button
                          type="button"
                          onClick={() => setSelected([])}
                          title="Clear selection"
                          aria-label="Clear selection"
                        >
                          <X size={12} />
                        </button>
                      </span>
                      {canWhatsApp && canEdit && (
                        <AppButton size="sm" className="eq-wa-btn" onClick={() => setShowWhatsApp(true)}>
                          <MessageCircle size={14} /> Send WhatsApp
                        </AppButton>
                      )}
                      {canChangeBranch && (
                        <AppButton variant="secondary" size="sm" onClick={() => setShowBranchTransfer(true)}>
                          <ArrowRightLeft size={14} /> Change Branch
                        </AppButton>
                      )}
                    </>
                  )}
                </FilterToolbar>
              }
              sort={sort}
              onSortChange={(key, direction) => { setSort({ key, direction }); setCurrentPage(1); }}
              columnFilters={Object.fromEntries(
                Object.entries(COLUMN_FILTERS).map(([key, m]) => [key, m.toValue(filters)])
              )}
              onColumnFilterChange={(key, value) => {
                const map = COLUMN_FILTERS[key];
                if (!map) return;
                // `filters` stays the single source of truth — the drawer, the
                // KPI cards and sessionStorage all read it, so a header funnel
                // writes there rather than keeping a second copy.
                setFilters((prev) => ({ ...prev, ...map.toFilters(value) }));
                setCurrentPage(1);
              }}
              /* Selection drives bulk WhatsApp as well as branch transfer, so it
                 is no longer conditional on the institute being multi-branch. */
              selectable={(canWhatsApp && canEdit) || canChangeBranch}
              selectedRows={selected}
              onSelectionChange={setSelected}
              columns={[
                {
                  key: "id",
                  label: "ID",
                  /* ⚠️ The class is on the SPAN, not on the `<td>`. AppDataTable
                     styles its own cells with `[&_tbody_td]:…` descendant rules,
                     which are (0,1,2); a `columns[].className` lands on the td at
                     (0,1,0) and loses. `eq-cell-id` set the size, the ink, the
                     weight and the family from there — and only the last two
                     survived, so this column rendered at the body size, in body
                     ink, in whatever the browser calls `monospace`. */
                  render: (e) => <span className="eq-id">{e._id.slice(-6).toUpperCase()}</span>,
                },
                {
                  key: "name",
                  label: "Student Name",
                  sortable: true,
                  // The endpoint's `search` reads full name, email AND course
                  // name — the placeholder says so, because a filter that
                  // silently matches more than its column is one people stop
                  // trusting when a row they did not expect appears.
                  filter: { type: "text", placeholder: "Name, email or course…" },
                  /* A real <button>, not a div carrying role="button" — which
                     needed a hand-written tabIndex, Enter/Space handler and
                     cursor to imitate one, and is what `lint:components
                     --check divbutton` counts.
                     ⚠️ No inline colour. The name was hardcoded to #2563eb, the
                     v1 blue, so the one link in every row was the one thing on
                     the page that could not follow the institute's brand. */
                  render: (e) => (
                    <button
                      type="button"
                      className="eq-cell-name"
                      onClick={() => navigate(`/enquiries/${e._id}`)}
                      title="View enquiry"
                    >
                      <span className="eq-cell-name__full">{e.fullName}</span>
                      <span className="eq-cell-name__email">{e.email}</span>
                    </button>
                  ),
                },
                {
                  key: "course",
                  label: "Course",
                  // `courses` carries a leading "All Courses" entry for the
                  // drawer's select; a set filter shows every option as a
                  // checkbox, so an "all" row there would be a box that means
                  // the same as no boxes.
                  filter: { type: "set", options: courses.filter((c) => c.value) },
                  render: (e) => e.course?.name || e?.otherCourse || "-",
                },
                {
                  key: "contact",
                  label: "Contact",
                  sortable: true,
                  filter: { type: "text", placeholder: "Mobile number…" },
                  render: (e) => (
                    <span className="eq-cell-contact">
                      {e.mobile}
                      {/* One-click dial straight from the list — the most common
                          path a counsellor actually takes. */}
                      {actions.canCall && e.mobile && (
                        <button
                          type="button"
                          className="eq-cell-contact__call"
                          title={`Call ${e.fullName || e.mobile}`}
                          aria-label={`Call ${e.fullName || e.mobile}`}
                          onClick={(ev) => { ev.stopPropagation(); actions.callEnquiry(e); }}
                        >
                          <Phone size={13} />
                        </button>
                      )}
                    </span>
                  ),
                },
                {
                  key: "source",
                  label: "Source",
                  className: "eq-cell-source",
                  filter: { type: "set", options: SOURCE_OPTIONS },
                  render: (e) => <EnquirySourceBadge enquiry={e} />,
                },
                {
                  // Exists because the drawer had a Campaign text input and the
                  // drawer is gone. It is also the only place the ad campaign a
                  // lead came from is visible at all — it was filterable but
                  // never shown, which is a filter you cannot check the result of.
                  key: "campaign",
                  label: "Campaign",
                  filter: { type: "text", placeholder: "Campaign name or id…" },
                  render: (e) => e.campaign?.name || <span className="eq-cell-muted">—</span>,
                },
                {
                  key: "date",
                  label: "Date",
                  sortable: true,
                  // Writes BOTH `startDate` and `endDate`, which is also what
                  // the range presets above the table set — so the two cannot
                  // show different ranges.
                  filter: "date",
                  /* `eq-cell-date` was a td class, so its muted ink lost to the
                     table's own `tbody td` rule and this column rendered at the
                     same strength as the name beside it. It was also a second
                     declaration of a class `Settings/settings.scss` owns for the
                     Zoho sync log. On the span, it is neither. */
                  render: (e) => (
                    <span className="eq-date">
                      {e.createdAt
                        ? new Date(e.createdAt).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })
                        : "—"}
                    </span>
                  ),
                },
                {
                  key: "followUp",
                  label: "Next Follow-Up",
                  className: "eq-cell-followup",
                  // Sortable, deliberately NOT filterable: "who is overdue" is
                  // the question here and the endpoint has no parameter for it,
                  // so a funnel would be a control that does nothing.
                  sortable: true,
                  // Reads the denormalised Enquiry.nextFollowUpAt mirror, so the
                  // list costs no extra query per row.
                  render: (e) => (e.nextFollowUpAt ? <FollowUpDue dueAt={e.nextFollowUpAt} /> : <span className="eq-cell-muted">—</span>),
                },
                {
                  key: "status",
                  label: "Status",
                  sortable: true,
                  // The institute's own configured pipeline, not a hardcoded
                  // list — see useEnquiryStages.
                  filter: { type: "set", options: stageNames.map((n) => ({ value: n, label: n })) },
                  render: (e) => (
                    <>
                      <StatusBadge status={(e.status || "Enquired").toLowerCase()}>
                        {e.status || "Enquired"}
                      </StatusBadge>
                      {/* ⚠️ Was a Bootstrap `.badge` at an inline 0.65rem — a
                          THIRD badge spec in this one row, and the inline size
                          was there because `_bootstrap-skin.scss` styles
                          `.badge:not(.ui-badge):not(.app-badge)` at (0,3,0) and
                          out-specifies any class a page can write. AppBadge is
                          named in that `:not()`, so it needs no fight and comes
                          out at the same size and weight as the StatusBadge
                          beside it. */}
                      {e.zohoSyncStatus && (
                        <AppBadge
                          size="sm"
                          className="ms-1"
                          color={ZOHO_SYNC_COLOR[e.zohoSyncStatus]}
                          variant={ZOHO_SYNC_COLOR[e.zohoSyncStatus] ? "default" : "secondary"}
                          title={`Zoho: ${e.zohoSyncStatus}`}
                        >
                          {e.zohoSyncStatus === "synced" ? "Z✓" : e.zohoSyncStatus === "pending" ? "Z⏳" : "Z✗"}
                        </AppBadge>
                      )}
                    </>
                  ),
                },
                {
                  key: "assigned",
                  label: "Assigned",
                  className: "eq-cell-assigned",
                  filter: {
                    type: "set",
                    options: [
                      { value: "unassigned", label: "Unassigned" },
                      ...assignees.map((a) => ({
                        value: String(a._id),
                        label: [a.firstName, a.lastName].filter(Boolean).join(" ") || a.email,
                      })),
                    ],
                  },
                  /* `AppBadge`, not a hand-rolled chip. The chip it replaces was
                     0.75rem/500 sitting one column from a StatusBadge at
                     0.6875rem/600 — a second badge spec, on the same row, for no
                     reason. `eq-assigned-chip` now adds only the truncation a
                     name needs in a resizable column. */
                  render: (e) =>
                    e.assignedTo ? (
                      <AppBadge size="sm" color="primary" className="eq-assigned-chip" title={e.assignedTo.email}>
                        <span className="eq-assigned-chip__name">
                          {`${e.assignedTo.firstName || ''} ${e.assignedTo.lastName || ''}`.trim() || e.assignedTo.email || '—'}
                        </span>
                      </AppBadge>
                    ) : (
                      <AppBadge size="sm" variant="secondary" className="eq-assigned-chip">—</AppBadge>
                    ),
                },
                {
                  key: "notes",
                  label: "Notes",
                  /* No className: the cell holds one AppButton, and the
                     `max-width` the old `eq-cell-notes` set is inert anyway —
                     `resizable` puts the table in `table-layout: fixed`, where
                     the <colgroup> decides the width. */
                  render: (e) => {
                    const noteCount = e.followUpNotes?.length || 0;
                    const hasNotes = noteCount > 0 || !!e.comments;
                    const isOpen = showAllNotes || expandedNotes.has(e._id);
                    return (
                      <>
                        {hasNotes && (
                          <AppButton
                            variant={isOpen ? "outline-primary" : "outline"}
                            size="xs"
                            onClick={(ev) => { ev.stopPropagation(); toggleNoteRow(e._id); }}
                            aria-expanded={isOpen}
                          >
                            <StickyNote size={12} />
                            Notes{noteCount > 0 ? ` (${noteCount})` : ""}
                            {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                          </AppButton>
                        )}
                      </>
                    );
                  },
                },
                {
                  key: "actions",
                  label: "",
                  // `align` already emits the right-alignment `eq-cell-actions`
                  // restated, and a cell holding one icon button cannot wrap.
                  align: "right",
                  // Frozen: this table is wider than the viewport, and without
                  // it you scroll down to the row you want and then sideways to
                  // act on it, losing the row on the way.
                  pinned: "right",
                  width: 48,
                  resizable: false,
                  render: (e) => (
                    <ActionMenu trigger={<MoreHorizontal size={16} />}>
                      {actions.renderItems(e)}
                    </ActionMenu>
                  ),
                },
              ]}
              onRowContextMenu={openContextMenu}
              /* ⚠️ `totalPages` and `onChange` — `AppDataTable` hands this
                 straight to `ui/Paginator`, whose props are not
                 `Common/DataTable`'s. That one derived the page count itself
                 and called `onPageChange`. */
              pagination={{
                page: currentPage,
                /* The SERVER's count, which this page already stores and had
                   stopped reading — `Common/DataTable` derived its own from
                   total/pageSize. Recomputing it here would be a second answer
                   to a question the API already answers. */
                totalPages,
                pageSize,
                total: totalRecords,
                onChange: setCurrentPage,
                infoLabel: "enquiries",
                onPageSizeChange: (size) => { setPageSize(size); setCurrentPage(1); },
                pageSizeOptions: [10, 25, 50, 100],
              }}
              renderExpandedRow={(e) => {
                const isOpen = showAllNotes || expandedNotes.has(e._id);
                const lastNote = e.followUpNotes?.length
                  ? e.followUpNotes[e.followUpNotes.length - 1]
                  : null;
                if (!isOpen || (!lastNote && !e.comments)) return null;
                /* ⚠️ Returns CONTENT, not a `<tr>`. `AppDataTable` owns the row
                   and the full-width cell — the `<tr className="eq-notes-row">
                   <td colSpan={99}>` this used to return was the old
                   `Common/DataTable` contract, where the call site built its own
                   row and a returned `<div>` would have broken the table. */
                return (
                  <div className="eq-notes-panel">
                        {lastNote ? (
                          <>
                            <div className="eq-notes-panel__head">
                              <strong>{lastNote.channel === "whatsapp" ? "Latest activity" : "Latest note"}</strong>
                              {lastNote.channel === "whatsapp" && (
                                <span className={`eq-note-channel eq-note-channel--${lastNote.meta?.status === "failed" ? "failed" : "sent"}`}>
                                  <MessageCircle size={11} />
                                  WhatsApp
                                </span>
                              )}
                              {e.followUpNotes.length > 1 && (
                                <span className="eq-notes-panel__count">
                                  {e.followUpNotes.length} notes total
                                </span>
                              )}
                              {lastNote.createdAt && (
                                <span className="eq-notes-panel__date">
                                  {new Date(lastNote.createdAt).toLocaleString()}
                                </span>
                              )}
                            </div>
                            <p className="eq-notes-panel__text">{lastNote.note}</p>
                          </>
                        ) : (
                          <>
                            <div className="eq-notes-panel__head"><strong>Comments</strong></div>
                            <p className="eq-notes-panel__text">{e.comments}</p>
                          </>
                        )}
                  </div>
                );
              }}
            />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Enquiry"
        message="Are you sure you want to delete this enquiry? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={deleting}
      />

      <EnquiryWhatsAppModal
        show={showWhatsApp}
        onHide={() => setShowWhatsApp(false)}
        enquiries={selectedEnquiries}
        showToast={(message, type = "success") => setToast({ show: true, message, type })}
        onSent={() => {
          setSelected([]);
          // Sends are queued, so the notes land a moment later — refresh anyway
          // so already-delivered ones show up without a manual reload.
          setRefreshKey((k) => k + 1);
        }}
      />

      <TransferBranchModal
        show={showBranchTransfer}
        onHide={() => setShowBranchTransfer(false)}
        selectedIds={selected}
        mode="enquiries"
        onTransfer={() => {
          setSelected([]);
          setRefreshKey(k => k + 1);
          setToast({ show: true, message: "Enquiries moved to new branch!", type: "success" });
        }}
      />

      {/* Right-click row actions */}
      {contextMenu && (
        <EnquiryContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
        >
          {actions.renderItems(contextMenu.enquiry)}
        </EnquiryContextMenu>
      )}

      {/* Shared add-note + convert dialogs for the kebab & context menus */}
      {actions.modals}
    </div>
  );
};

export default EnquiriesList;
